import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Container, PageHeader, Spinner } from "@/components/shared";
import { toast } from "sonner";
import { Search, Quote, BookOpen, Users, Clock, Scale, Landmark, ListChecks, Mic, HelpCircle, Sparkles, BookMarked, ArrowRight } from "lucide-react";

const CAT_ICON = {
  "Citations": Quote, "Histoire": BookOpen, "Personnalités": Users, "Chronologies": Clock,
  "Décisions de justice": Scale, "Droits et libertés": Landmark, "Méthodes": ListChecks,
  "Discours historiques": Mic, "Questions fréquentes": HelpCircle, "Culture générale": Sparkles,
  "Livres recommandés": BookMarked,
};

export default function Library() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (category, query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/knowledge", { params: { category: category || undefined, q: query || undefined, limit: 60 } });
      setEntries(data.entries); setCategories(data.categories); setTotal(data.total);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load("", ""); }, [load]);

  const onSearch = (e) => { e?.preventDefault?.(); load(cat, q); };
  const pickCat = (c) => { const n = cat === c ? "" : c; setCat(n); load(n, q); };

  return (
    <Container>
      <PageHeader eyebrow="Base de connaissances" title="Base de connaissances"
        subtitle="Citations, histoire, décisions de justice, méthodes, droits et libertés — une base fiable et régulièrement enrichie." />

      <form onSubmit={onSearch} className="card-surface p-4 mb-5 flex flex-wrap gap-2">
        <div className="flex-1 min-w-0 basis-full sm:basis-0 flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-3">
          <Search size={18} className="text-white/40 shrink-0" />
          <input data-testid="kb-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une citation, une personnalité, un thème, un mot-clé…"
            className="flex-1 min-w-0 bg-transparent py-2.5 outline-none text-sm placeholder:text-white/30" />
        </div>
        <button data-testid="kb-search-button" type="submit" className="gold-gradient text-black rounded-xl px-5 py-2.5 text-sm font-medium w-full sm:w-auto shrink-0">Rechercher</button>
      </form>

      <div className="flex flex-wrap gap-2 mb-6">
        <button data-testid="kb-cat-all" onClick={() => pickCat("")}
          className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${!cat ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60 hover:text-white"}`}>
          Tout ({total && !cat && !q ? total : categories.reduce((a, c) => a + c.count, 0)})
        </button>
        {categories.filter((c) => c.count > 0).map((c) => {
          const Icon = CAT_ICON[c.name] || BookOpen;
          return (
            <button key={c.name} data-testid={`kb-cat-${c.name}`} onClick={() => pickCat(c.name)}
              className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors flex items-center gap-1.5 ${cat === c.name ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60 hover:text-white"}`}>
              <Icon size={14} /> {c.name} ({c.count})
            </button>
          );
        })}
      </div>

      {loading && <Spinner label="Chargement..." />}
      {!loading && entries.length === 0 && <p className="text-white/40 text-sm">Aucune fiche ne correspond à votre recherche.</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kb-grid">
        {entries.map((e) => {
          const Icon = CAT_ICON[e.category] || BookOpen;
          const isQuote = e.category === "Citations";
          return (
            <button key={e.entry_id} data-testid={`kb-card-${e.entry_id}`} onClick={() => navigate(`/library/${e.entry_id}`)}
              className="card-surface p-5 text-left hover:border-gold/30 transition-colors group flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full bg-white/[0.05] text-white/60"><Icon size={12} /> {e.category}</span>
              </div>
              {isQuote ? (
                <p className="text-white/90 text-[15px] leading-relaxed italic line-clamp-4">{e.content}</p>
              ) : (
                <>
                  <h3 className="font-display text-base mb-1 group-hover:text-gold transition-colors line-clamp-2">{e.title}</h3>
                  <p className="text-white/45 text-sm line-clamp-3 leading-relaxed">{e.explanation || e.content}</p>
                </>
              )}
              <div className="mt-auto pt-3 flex items-center justify-between">
                <span className="text-gold/80 text-xs">{e.author || e.theme}</span>
                <ArrowRight size={15} className="text-white/30 group-hover:text-gold transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </Container>
  );
}
